import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Search, Users } from "lucide-react";
import { formatDayLabel } from "@/lib/format";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { QueryBoundary } from "@/components/ui/QueryBoundary";
import { EmptyState, TableSkeleton } from "@/components/ui/states";
import { fetchCustomers } from "./api";

const PAGE_SIZE = 25;

export function CustomersPage() {
  const { org } = useActiveOrg();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => setPage(0), [debounced, org?.organizationId]);

  const query = useQuery({
    queryKey: ["customers", org?.organizationId, debounced, page],
    queryFn: () =>
      fetchCustomers({
        organizationId: org!.organizationId,
        search: debounced,
        page,
        pageSize: PAGE_SIZE,
      }),
    enabled: Boolean(org),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="flex flex-col gap-3">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <h1 className="text-head font-semibold tracking-tight">Clientes</h1>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-ink-3" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nombre o teléfono"
            aria-label="Buscar clientes"
            className="w-full rounded-md border border-line bg-surface py-1.5 pl-8 pr-3 text-ui sm:w-64"
          />
        </div>
      </header>

      <Panel bleed>
        <QueryBoundary
          query={query}
          skeleton={<TableSkeleton rows={8} cols={4} />}
          isEmpty={(d) => d.rows.length === 0}
          empty={
            <EmptyState
              icon={<Users className="size-5" />}
              title={debounced ? "Ningún cliente coincide" : "Aún no tienes clientes"}
              description={
                debounced
                  ? "Prueba con otro nombre o teléfono."
                  : "Se crean solos con el primer pedido: no hace falta registrarlos."
              }
            />
          }
        >
          {(data) => (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] border-collapse text-ui">
                  <thead>
                    <tr>
                      {["Cliente", "Teléfono", "Correo", "Alta"].map((h) => (
                        <th
                          key={h}
                          className="border-b border-line bg-sunk px-4 py-2.5 text-left font-mono text-mark font-medium uppercase tracking-[0.1em] text-ink-3"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((c) => (
                      <tr key={c.id} className="border-b border-line-2 last:border-0">
                        <td className="px-4 py-3 font-medium">
                          {[c.first_name, c.last_name].filter(Boolean).join(" ")}
                        </td>
                        <td className="tnum px-4 py-3 text-ink-2">{c.phone ?? "—"}</td>
                        <td className="px-4 py-3 text-ink-2">{c.email ?? "—"}</td>
                        <td className="px-4 py-3 text-ink-3">
                          {formatDayLabel(c.created_at, org?.timezone)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between gap-3 border-t border-line-2 px-4 py-2.5">
                <span className="tnum text-xs text-ink-3">{data.total} clientes</span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => setPage(page - 1)}>
                    Anterior
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={(page + 1) * PAGE_SIZE >= data.total}
                    onClick={() => setPage(page + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            </>
          )}
        </QueryBoundary>
      </Panel>
    </div>
  );
}
